/// <amd-module name="Dashboards/DashboardView/src/redux/selectors/SelectorHelpers" />
import { Widget } from "../../model/Widget";
import { WidgetMap } from "../../model/WidgetMap";

import {
    contributionHasModernImplementation,
    isInternalContribution,
    widgetImplementationIsSlow
} from "Dashboards/WidgetCatalog/WidgetContributionsHelper";

import { IVssPageContext } from "VSS/Platform/Context";

/**
 * An internal widget (a.k.a. first-party widget) is a widget developed internally at Microsoft
 * and published using the 'ms' publisher.
 */
export function isInternalWidget(widgetDatum: Widget): boolean {
    return isInternalContribution(widgetDatum.contributionId);
}

/** A widget is a modern widget if its implementation is written using the modern web platform. */
export function isModernWidget(widgetDatum: Widget, pageContext: IVssPageContext): boolean {
    return contributionHasModernImplementation(pageContext, widgetDatum.contributionId);
}

/**
 * A widget is a slow widget if it is on the hard-coded list of slow widgets.
 * This list is based on observed TTI values of dashboards that consist of a single widget for each widget.
 */
export function isSlowWidget(widgetDatum: Widget, pageContext: IVssPageContext): boolean {
    return widgetImplementationIsSlow(widgetDatum.contributionId, pageContext);
}

export function toBooleanAssociativeArray(trueKeys: ReadonlyArray<string>, allKeys: ReadonlyArray<string>): Readonly<WidgetMap<boolean>> {
    const map: { [key: string]: boolean } = {};
    for (const key of allKeys) {
        map[key] = false;
    }

    for (const key of trueKeys) {
        if (map.hasOwnProperty(key)) {
            map[key] = true;
        }
    }

    return map;
}

export function filterUsingAssociativeArray(keys: ReadonlyArray<string>, map: Readonly<WidgetMap<boolean>>): ReadonlyArray<string> {
    const filteredArray: string[] = [];
    for (const key of keys) {
        if (map[key]) {
            filteredArray.push(key);
        }
    }

    return filteredArray;
}

export function flipBooleanAssociativeArrayValues(map: Readonly<WidgetMap<boolean>>): Readonly<WidgetMap<boolean>> {
    const flippedMap: { [key: string]: boolean } = {};
    for (const key in map) {
        flippedMap[key] = !map[key];
    }
    return flippedMap;
}

function widgetComparer(a: Widget, b: Widget): boolean {
    return (
        a === b ||
        (a.id === b.id &&
            a.name === b.name &&
            a.settings === b.settings &&
            a.settingsVersion.major === b.settingsVersion.major &&
            a.settingsVersion.minor === b.settingsVersion.minor &&
            a.settingsVersion.patch === b.settingsVersion.patch &&
            a.position.column === b.position.column &&
            a.position.row === b.position.row &&
            a.size.columnSpan === b.size.columnSpan &&
            a.size.rowSpan === b.size.rowSpan)
    );
}

export function widgetListComparer(a: ReadonlyArray<Widget>, b: ReadonlyArray<Widget>): boolean {
    let areEqual: boolean = a.length === b.length;

    for (let i = 0; i < a.length && areEqual; ++i) {
        areEqual = widgetComparer(a[i], b[i]);
    }

    return areEqual;
}
